import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import GroupsIcon from '@mui/icons-material/Groups'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BigModal from '../../../../components/Modals/BigModal'
import API from '../../../../services/API'
import UploadParticipants from './UploadParticipants'
import { EventMetaContext } from '../SingleEvent'

// Organiser-supplied entry list (bib numbers + runner details). Each upload
// replaces the stored list; the rows live in their own collection and only
// the meta travels with the event.
const ParticipantsPanel = () => {
  const { eventMeta } = useContext(EventMetaContext)
  const [meta, setMeta] = useState(eventMeta.participantList ?? null)
  const [openUpload, setOpenUpload] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState('')

  useEffect(() => {
    setMeta(eventMeta.participantList ?? null)
  }, [eventMeta.participantList])

  const hasList = !!meta?.count

  const removeList = async () => {
    setRemoving(true)
    setRemoveError('')
    const res = await API.delete(`/events/${eventMeta._id}/participants`)
    setRemoving(false)
    if (res.status !== 200) {
      setRemoveError(res.data?.error || 'Could not remove the participant list.')
      return
    }
    setMeta(null)
    setConfirmRemove(false)
  }

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        bgcolor: 'background.paper',
      }}
    >
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Stack gap={0.25}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Participants
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Entry list for ePCR bib-number lookup
            </Typography>
          </Stack>
          <Tooltip title="Upload a participant list">
            <IconButton
              size="small"
              color="primary"
              onClick={() => setOpenUpload(true)}
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {!hasList ? (
          <Stack
            alignItems="center"
            gap={1}
            sx={{
              py: 2.5,
              px: 2,
              borderRadius: 2,
              border: '1px dashed rgba(15, 23, 42, 0.18)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              No participant list yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload the organiser's entry list (CSV) and crews writing an
              ePCR at this event can pre-fill patient details from a bib
              number.
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenUpload(true)}
              sx={{ mt: 0.5 }}
            >
              Upload list
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                bgcolor: 'anGreen.main',
                color: 'anGreen.contrastText',
                opacity: 0.9,
              }}
            >
              <GroupsIcon fontSize="small" />
            </Box>
            <Box minWidth={0} flex={1}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {meta.fileName || 'Participant list'}
              </Typography>
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                noWrap
              >
                {[
                  `${meta.count} participants`,
                  meta.uploadedAt &&
                    moment(meta.uploadedAt).format('Do MMM YYYY'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            <Tooltip title="Remove the participant list">
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirmRemove(true)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <BigModal autoWidth open={openUpload} onClose={() => setOpenUpload(false)}>
        <UploadParticipants
          eventID={eventMeta._id}
          existingCount={meta?.count || 0}
          onClose={() => setOpenUpload(false)}
          onUploaded={(newMeta) => setMeta(newMeta)}
        />
      </BigModal>

      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)}>
        <DialogTitle>Remove participant list?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes all {meta?.count} participants from this event. ePCRs
            that already copied details keep them, but crews will no longer be
            able to search by bib number.
          </Typography>
          {removeError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {removeError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(false)} disabled={removing}>
            Cancel
          </Button>
          <Button
            onClick={removeList}
            color="error"
            variant="contained"
            disabled={removing}
          >
            {removing ? 'Removing...' : 'Remove List'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default React.memo(ParticipantsPanel)
